import { useEffect, useState } from "react";
import { FiX } from "react-icons/fi";

interface Props {
  files: readonly File[];
  onRemove: (index: number) => void;
  disabled?: boolean;
  surface?: "desktop" | "mobile";
}

export const AttachmentPreviewList: React.FunctionComponent<Props> = ({
  files,
  onRemove,
  disabled = false,
  surface = "mobile",
}: Props) => {
  if (files.length === 0) return null;

  return (
    <ol
      className="grid max-w-full grid-cols-1 gap-2 sm:grid-cols-3"
      aria-label="添付ファイル（選択順）"
      data-surface={surface}
    >
      {files.map((file, index) => (
        <AttachmentPreviewItem
          // A user can select the same file twice; index keeps both ordered entries.
          // biome-ignore lint/suspicious/noArrayIndexKey: Duplicate File objects have no unique identifier beyond their ordered position.
          key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
          file={file}
          index={index}
          count={files.length}
          disabled={disabled}
          onRemove={onRemove}
        />
      ))}
    </ol>
  );
};

export function AttachmentPreviewItem({
  file,
  index,
  count,
  disabled,
  onRemove,
}: {
  file: File;
  index: number;
  count: number;
  disabled: boolean;
  onRemove: (index: number) => void;
}): React.ReactNode {
  return (
    <li className="relative min-w-0 rounded-lg border border-border bg-muted/40 p-2">
      <header className="mb-1 flex items-start justify-between gap-1">
        <data className="text-xs font-medium text-foreground" value={index + 1}>
          {index + 1}番目 / {count}
        </data>
        <button
          type="button"
          onClick={(): void => onRemove(index)}
          disabled={disabled}
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
          aria-label={`${index + 1}番目の添付「${file.name}」を削除`}
        >
          <FiX className="h-4 w-4" aria-hidden="true" />
        </button>
      </header>
      <p className="break-all text-xs text-muted-foreground">{file.name}</p>
      <AttachmentFilePreviewMedia file={file} order={index + 1} />
      <p className="text-xs text-muted-foreground">
        {(file.size / 1024).toFixed(1)}KB
        {file.type.startsWith("video/") ? "・動画" : "・画像"}
      </p>
    </li>
  );
}

function AttachmentFilePreviewMedia({
  file,
  order,
}: {
  file: File;
  order: number;
}): React.ReactNode {
  const [url] = useState(() => URL.createObjectURL(file));
  useEffect(() => (): void => URL.revokeObjectURL(url), [url]);

  return file.type.startsWith("video/") ? (
    <video
      src={url}
      muted
      controls
      preload="metadata"
      className="my-1 h-24 w-full rounded object-contain"
      aria-label={`${order}番目の添付動画プレビュー`}
    />
  ) : (
    <img
      src={url}
      alt={`${order}番目の添付画像プレビュー`}
      className="my-1 h-24 w-full rounded object-contain"
    />
  );
}
